import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import apiService from '../../services/api';
import { DATE_FORMATS, ERROR_MESSAGES } from '../../constants';
import CommentsSection from './create-support/CommentsSection';
import useCodeCommon from '../../hooks/useCodeCommon';
import useSupportUsers from '../../hooks/useSupportUsers';
import { useAppSelector } from '../../hooks/useAuth';
import CustomLoading from '../../components/common/CustomLoading';
import useRBAC from '../../hooks/useRBAC';
import { formatMobileNumber } from '../../utils/formatters';

const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
const DEFAULT_IMAGE = "default.png";

interface SupportTicketDetails {
  ticketMasterId: string;
  ticketId: string;
  userName: string;
  phoneNumber: string;
  createdAt: string;
  hubName: string;
  issueSummary: string;
  description: string;
  status: string;
  statusId: number;
  priority: string;
  assignee: string;
  attachments?: string[];
  ticketDetails?: Array<{
    supportticketdetailid: number;
    supportticketid: number;
    assignee: number;
    statusid: number;
    note: string;
    medias: string[];
    userid: number;
    enteredon: string;
  }>;
}

const SelectInput = ({
  field,
  formData,
  onChange,
  options,
  label,
  required = false,
  userOptions,
}: {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  options: string[];
  label?: string;
  required?: boolean;
  userOptions?: Array<{userid: number, name: string}>;
}) => (
  <FormControl fullWidth size="small" required={required}>
    <InputLabel
      sx={{
        "&.MuiInputLabel-asterisk": {
          color: "red",
        },
      }}
    >
      {label || "(Select)"}
    </InputLabel>
    <Select
      value={formData[field]}
      onChange={(e) => onChange(field, e.target.value)}
      label={label}
    >
      {userOptions ? (
        userOptions.map((user) => (
          <MenuItem key={user.userid} value={user.userid.toString()}>
            {user.name}
          </MenuItem>
        ))
      ) : (
        options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))
      )}
    </Select>
  </FormControl>
);

const SupportDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const { isSupport } = useRBAC();
  const [assign, setAssign] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [originalPriority, setOriginalPriority] = useState('');
  const [addNote, setAddNote] = useState('');
  const [ticketDetails, setTicketDetails] = useState<SupportTicketDetails | null>(null);
  const [originalTicketData, setOriginalTicketData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [medias, setMedias] = useState<string[]>([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedMediaType, setSelectedMediaType] = useState<'image' | 'video' | null>(null);
  

  const { 
    options: allSupportStatusOptions, 
    loading: supportStatusLoading, 
    error: supportStatusError 
  } = useCodeCommon("SupportTicketStatus");
  
  const { 
    options: allPriorityOptions, 
    loading: priorityLoading, 
    error: priorityError 
  } = useCodeCommon("TicketPriority");
  
  const { 
    users: assignableUsers, 
    loading: usersLoading, 
    error: usersError 
  } = useSupportUsers(4);
  
  const supportStatusOptions = allSupportStatusOptions;
  
  const ticketId = new URLSearchParams(location.search).get('id') || 
                   (location.state as any)?.ticketId || 
                   '';

  const getSupportStatusDisplayName = (statusid: number): string => {
    const statusOption = allSupportStatusOptions.find(
      (option) => Number.parseInt(option.codevalue, 10) === statusid
    );
    return statusOption ? statusOption.codedisplayname : '';
  };

  const getSupportStatusCodeValue = (statusDisplayName: string): number => {
    const statusOption = supportStatusOptions.find(
      (option) => option.codedisplayname === statusDisplayName
    );
    const codeValue = statusOption ? Number.parseInt(statusOption.codevalue, 10) : 0;
    return codeValue;
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "Open":
        return {
          backgroundColor: "rgba(227, 242, 253, 1)",
          color: "rgba(25, 118, 210, 1)",
          fontWeight: 600,
        };
      case "InProgress":
        return {
          backgroundColor: "rgba(255, 248, 220, 1)",
          color: "rgba(184, 134, 11, 1)",
          fontWeight: 600,
        };
      case "Resolved":
      case "Closed":
      case "Refunded":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Awaiting User":
      case "Pending":
        return {
          backgroundColor: "rgba(255, 245, 157, 1)",
          color: "rgba(107, 105, 14, 1)",
          fontWeight: 600,
        };
      case "Failed":
        return {
          backgroundColor: "rgba(255, 227, 227, 1)",
          color: "rgba(197, 0, 0, 1)",
          fontWeight: 600,
        };
      default:
        return {
          backgroundColor: "rgba(245, 245, 245, 1)",
          color: "rgba(117, 117, 117, 1)",
          fontWeight: 600,
        };
    }
  };

  const getPriorityDisplayName = (priorityid: number): string => {
    const priorityOption = allPriorityOptions.find(
      (option) => Number.parseInt(option.codevalue, 10) === priorityid
    );
    return priorityOption ? priorityOption.codedisplayname : '';
  };

  const getPriorityCodeValue = (priorityDisplayName: string): number => {
    const priorityOption = allPriorityOptions.find(
      (option) => option.codedisplayname === priorityDisplayName
    );
    return priorityOption ? Number.parseInt(priorityOption.codevalue, 10) : 0;
  };

  const getAssigneeDisplayName = (assigneeId: number): string => {
    const assigneeUser = assignableUsers.find(
      (user) => user.userid === assigneeId
    );
    return assigneeUser ? assigneeUser.name : '';
  };

  const fetchTicketDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getSupportTicketDetails(ticketId);
      
      const data = (response as any)?.data || response;
      
      if (data) {
        const statusDisplayName = getSupportStatusDisplayName(data?.statusid);
        const priorityDisplayName = getPriorityDisplayName(data?.ticketpriorityid || null);
        const assigneeDisplayName = getAssigneeDisplayName(data?.assignee || 0);
        
        const transformedDetails: SupportTicketDetails = {
          ticketMasterId: data?.ticketmasterid?.toString() || data?.id?.toString() || ticketId || '',
          ticketId: data?.ticketid || '',
          userName: data?.username || '',
          phoneNumber: formatMobileNumber(data?.phoneno) || '',
          createdAt: DATE_FORMATS.formatDateTime(data?.enteredon || ''),
          hubName: data?.hubname || '',
          issueSummary: data?.description || '',
          description: data?.description || '',
          status: statusDisplayName,
          statusId: data?.statusid,
          priority: priorityDisplayName,
          assignee: assigneeDisplayName,
          attachments: data?.medias || [],
          ticketDetails: data?.ticketDetails || [],
        };
        
        setTicketDetails(transformedDetails);
        setStatus(transformedDetails.status);
        setPriority(transformedDetails.priority);
        setOriginalPriority(transformedDetails.priority);
        setAssign(data?.assignee?.toString() || '');
        setOriginalTicketData(data); // Store original data for priority update
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch ticket details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails();
    }
  }, [ticketId]);

  // Sync form status/priority when options load
  useEffect(() => {
    if (originalTicketData) {
      if (allSupportStatusOptions.length > 0 && !status) {
         const name = getSupportStatusDisplayName(originalTicketData.statusid);
         if (name) setStatus(name);
      }
      if (allPriorityOptions.length > 0 && !priority) {
         const name = getPriorityDisplayName(originalTicketData.ticketpriorityid);
         if (name) setPriority(name);
      }
    }
  }, [originalTicketData, allSupportStatusOptions, allPriorityOptions, status, priority]);

  const handleBack = () => {
    const state = (location.state as any) || {};
    navigate('/support', {
      state: {
        searchTerm: state.searchTerm || '',
        selectedDate: state.selectedDate || null,
        assignee: state.assignee || '',
        priority: state.priority || '',
        status: state.status || '',
        category: state.category || '',
      },
    });
  };

  const handleUpdateStatus = async () => {
    if (!ticketDetails || !originalTicketData) {
      setSnackbar({
        open: true,
        message: 'Ticket details not available',
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      // For support role users, always use the original priority (they can't change it)
      const priorityToUse = isSupport() 
        ? (originalTicketData.ticketpriorityid || 0)
        : (getPriorityCodeValue(priority) || originalTicketData.ticketpriorityid || 0);
      
      const payload = {
        ticketmasterid: originalTicketData.ticketmasterid || 0,
        ticketid: originalTicketData.ticketid || '',
        userid: originalTicketData.enteredby || user?.userid || 0,
        enteredon: originalTicketData.enteredon || new Date().toISOString(),
        username: originalTicketData.username || '',
        phoneno: originalTicketData.phoneno || '',
        ticketcategoryid: originalTicketData.ticketcategoryid || 0,
        ticketpriorityid: priorityToUse,
        statusid: getSupportStatusCodeValue(status),
        hubmasterid: originalTicketData.hubmasterid || 0,
        hubname: originalTicketData.hubname || '',
        assignee: Number.parseInt(assign, 10) || originalTicketData.assignee || 0,
        description: originalTicketData.description || '',
        medias: originalTicketData.medias || [],
        bookingid: originalTicketData.bookingid || 0,
      };

      await apiService.updateSupportTicketDetails(payload);
      
      setSnackbar({
        open: true,
        message: 'Status updated successfully!',
        severity: 'success'
      });
      
      await fetchTicketDetails();
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || ERROR_MESSAGES.SERVER_ERROR,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!addNote.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a comment before posting',
        severity: 'error'
      });
      return;
    }

    if (!ticketDetails) {
      setSnackbar({
        open: true,
        message: 'Ticket details not available',
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        supportticketdetailid: 0,
        supportticketid: Number.parseInt(ticketDetails.ticketMasterId, 10) || 0,
        assignee: Number.parseInt(assign, 10) || 0,
        statusid: getSupportStatusCodeValue(status),
        note: addNote.trim(),
        medias: medias.map(media => normalizeImagePath(media)).filter(Boolean),
        userid: user?.userid || 0,
        enteredon: new Date().toISOString()
      };

      await apiService.saveSupportTicketNotes(payload);
      
      setSnackbar({
        open: true,
        message: 'Comment posted successfully!',
        severity: 'success'
      });
      
      setAddNote('');
      setMedias([]);
      
      await fetchTicketDetails();
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || ERROR_MESSAGES.SERVER_ERROR,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const normalizeImagePath = (value: string): string => {
    try {
      const trimmed = String(value || "").trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("[")) {
        const arr = JSON.parse(trimmed);
        if (Array.isArray(arr) && arr.length > 0) {
          return String(arr[0] || "");
        }
      }
      const lower = trimmed.toLowerCase();
      const marker = "/commonservice/images/";
      const idx = lower.indexOf(marker);
      if (idx !== -1) {
        const after = trimmed.substring(idx + marker.length);
        return after.startsWith("/") ? after : `/${after}`;
      }
      return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    } catch {
      return String(value || "");
    }
  };

  const resolvePreviewSrc = (value: string): string => {
    const v = String(value || "").trim();
    if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    if (v.startsWith('http')) return v;
    const cleaned = v.replace(/^\/+/, '');
    return `${BASE_IMAGE_URL}${cleaned}`;
  };

  const isVideoFile = (filename: string): boolean => {
    if (!filename) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
    const lowerFilename = filename.toLowerCase();
    return videoExtensions.some(ext => lowerFilename.endsWith(ext));
  };

  const handleMediaUpload = async (field: string, file: File) => {
    setUploadingMedia(true);
    try {
      const response = await apiService.uploadImage(file, 'support', 'media');
      let fileUrl = file.name;
      if (response?.data?.savedFiles && response.data.savedFiles.length > 0) {
        const savedFile = response.data.savedFiles[0];
        fileUrl = savedFile.filePath || savedFile.fileName || file.name;
      } else if (response?.data) {
        fileUrl = response.data.url || response.data.filePath || response.data.fileName || response.data.imageUrl || file.name;
      }
      setMedias(prev => [...prev, fileUrl]);
      setSnackbar({ open: true, message: 'Media file uploaded successfully!', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to upload media. Please try again.', severity: 'error' });
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDeleteMedia = (index: number) => {
    setMedias(prev => prev.filter((_, i) => i !== index));
  };


  if (loading) {
    return <CustomLoading message="Fetching Support Ticket Details..." />;
  }

  if (error || supportStatusError || usersError) {
    return (
      <Container fluid>
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error || supportStatusError || usersError}
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to Support
        </Button>
      </Container>
    );
  }

  if (!ticketDetails) {
    return (
      <Container fluid>
        <Alert severity="warning">
          No ticket details found.
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to Support
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'rgba(22, 67, 135, 1)', fontSize: 18, mb: 2 }}
          >
            Basic Information
          </Typography>
          <hr className="mb-3" />

          <Row className="gy-3">
            {[
              { label: 'Ticket ID', value: ticketDetails.ticketId },
              { label: 'User Name', value: ticketDetails.userName },
              { label: 'Mobile Number', value: formatMobileNumber(ticketDetails.phoneNumber) }, 
              { label: 'Created At', value: ticketDetails.createdAt },
              { label: 'Hub', value: ticketDetails.hubName },
            ].map((item, i) => (
              <Col key={i} xs={12} md={6} lg={3}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'gray', fontSize: 14, fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 600 }}>
                    {item.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </CardContent>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(22, 67, 135, 1)', fontSize: 18, mb: 2 }}>
            Body
          </Typography>
          <hr className="mb-3" />

          <Box mb={3}>
            <Typography variant="body2" sx={{ color: 'black', fontSize: 14, fontWeight: 700 }}>
              Description
            </Typography>
            <Typography variant="body1" sx={{ fontSize: 14, color: '#333', lineHeight: 1.6 }}>
              {ticketDetails.description}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'gray', fontSize: 14, fontWeight: 500, mb: 2 }}>
            Attachments
          </Typography>
          <Row className="gy-2">
            {ticketDetails.attachments && ticketDetails.attachments.length > 0 ? (
              ticketDetails.attachments.map((attachment, i) => {
                const mediaSrc = resolvePreviewSrc(attachment);
                const isVideo = isVideoFile(attachment);
                return (
                  <Col key={i} xs={6} md={3}>
                    <Box
                      onClick={() => {
                        setSelectedImage(mediaSrc);
                        setSelectedMediaType(isVideo ? 'video' : 'image');
                      }}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        height: 400,
                        overflow: 'hidden',
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2,
                        },
                      }}
                    >
                      {isVideo ? (
                        <>
                          <video
                            src={mediaSrc}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            controls={false}
                            muted
                            onError={(e) => {
                              (e.target as HTMLVideoElement).style.display = 'none';
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              backdropFilter: 'blur(4px)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                transform: 'translate(-50%, -50%) scale(1.1)',
                              },
                            }}
                          >
                            <PlayCircleIcon
                              sx={{
                                color: 'white',
                                fontSize: 50,
                                ml: 0.5, // Slight offset to center the play icon visually
                              }}
                            />
                          </Box>
                        </>
                      ) : (
                        <img
                          src={mediaSrc}
                          alt={`Attachment ${i + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
                          }}
                        />
                      )}
                    </Box>
                  </Col>
                );
              })
            ) : (
              <Col xs={12}>
                <Typography variant="body2" sx={{ color: 'gray', fontStyle: 'italic' }}>
                  No attachments available
                </Typography>
              </Col>
            )}
          </Row>
        </CardContent>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(22, 67, 135, 1)', fontSize: 18, mb: 2 }}>
            Comments
          </Typography>
          <hr className="mb-3" />
          
          <Row className="gy-3 mb-3">
            <Col xs={12} md={isSupport() ? 4 : 3}>
              <SelectInput
                field="assign"
                formData={{ assign }}
                onChange={(field, value) => setAssign(value)}
                label="Assign To"
                options={usersLoading ? ["Loading..."] : (assignableUsers && assignableUsers.length > 0 ? [] : ["No users available"])}
                userOptions={usersLoading ? undefined : (assignableUsers && assignableUsers.length > 0 ? assignableUsers : undefined)}
              />
            </Col>
            <Col xs={12} md={isSupport() ? 4 : 3}>
              <SelectInput
                field="status"
                formData={{ status }}
                onChange={(field, value) => setStatus(value)}
                label="Status"
                options={supportStatusLoading ? ["Loading..."] : (supportStatusOptions && supportStatusOptions.length > 0 ? supportStatusOptions.map(status => status.codedisplayname) : ["No status available"])}
              />
            </Col>
            {!isSupport() && (
              <Col xs={12} md={3}>
                <SelectInput
                  field="priority"
                  formData={{ priority }}
                  onChange={(field, value) => setPriority(value)}
                  label="Priority"
                  options={priorityLoading ? ["Loading..."] : (allPriorityOptions && allPriorityOptions.length > 0 ? allPriorityOptions.map(priority => priority.codedisplayname) : ["No priority available"])}
                />
              </Col>
            )}
            <Col xs={12} md={isSupport() ? 4 : 3}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUpdateStatus}
                  disabled={saving}
                  sx={{
                    backgroundColor: 'rgba(22, 67, 135, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(22, 67, 135, 0.8)',
                    },
                  }}
                >
                  {saving ? 'Updating...' : 'Update Status'}
                </Button>
              </Box>
            </Col>
          </Row>

          <CommentsSection
            addNote={addNote}
            saving={saving}
            onChangeNote={setAddNote}
            onPostComment={handleSaveNotes}
            ticketDetailsList={ticketDetails?.ticketDetails || []}
            medias={medias}
            uploading={uploadingMedia}
            onImageUpload={handleMediaUpload}
            onDeleteMedia={handleDeleteMedia}
            resolveSrc={resolvePreviewSrc}
            currentUserName={ticketDetails?.userName || ''}
            currentUserId={user?.userid}
          />
        </CardContent>
      </Paper>



      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            borderRadius: 2,
          }
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 2 }}>
          <IconButton
            onClick={() => {
              setSelectedImage(null);
              setSelectedMediaType(null);
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <>
              {selectedMediaType === 'video' ? (
                <video
                  src={selectedImage}
                  controls
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '85vh',
                    display: 'block',
                  }}
                />
              ) : (
                <img
                  src={selectedImage}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default SupportDetailsPage;
